import React, { createContext, useContext, useState, useCallback } from "react";

export type TaskType = "file-upload" | "url-import";
export type TaskStatus = "pending" | "running" | "completed" | "errored";

export interface Task {
  id: string;
  jobId: string;
  type: TaskType;
  status: TaskStatus;
  progress?: number;
  catalogueId: string;
  catalogueName: string;
  error?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface TasksContextType {
  tasks: Task[];
  addTask: (task: Omit<Task, "id" | "createdAt" | "updatedAt">) => string;
  updateTask: (id: string, updates: Partial<Task>) => void;
  removeTask: (id: string) => void;
  getPendingCount: () => number;
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

export function TasksProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([]);

  const generateId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const addTask = useCallback(
    (taskData: Omit<Task, "id" | "createdAt" | "updatedAt">) => {
      const id = generateId();
      const now = new Date();
      const newTask: Task = {
        ...taskData,
        id,
        createdAt: now,
        updatedAt: now,
      };
      setTasks((prev) => [newTask, ...prev]);
      return id;
    },
    []
  );

  const updateTask = useCallback((id: string, updates: Partial<Task>) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === id
          ? { ...task, ...updates, updatedAt: new Date() }
          : task
      )
    );
  }, []);

  const removeTask = useCallback((id: string) => {
    setTasks((prev) => prev.filter((task) => task.id !== id));
  }, []);

  const getPendingCount = useCallback(() => {
    return tasks.filter(
      (task) => task.status === "pending" || task.status === "running"
    ).length;
  }, [tasks]);

  return (
    <TasksContext.Provider
      value={{ tasks, addTask, updateTask, removeTask, getPendingCount }}
    >
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const context = useContext(TasksContext);
  if (context === undefined) {
    throw new Error("useTasks must be used within a TasksProvider");
  }
  return context;
}
